import { useMemo } from 'react';
import { HighnoteTransactionEvent } from 'API';
import { useCodingDisplay } from 'app/hooks/useCodingDisplay';
import { useCodingSystemConfig } from 'app/hooks/transactions/useCodingSystemConfig';
import { AGAVE_FIELD_MAP, FLEX_FIELD_MAP } from 'app/util/columnVisibility';
import { CODING_FIELD_CONFIGS, type CodingDisplayValues } from 'app/pages/mobile/types/mobileCodingTypes';
import { usePerProjectCostCodeNames } from 'app/hooks/usePerProjectCostCodeNames';

/**
 * Builds a CodingDisplayValues object from any transaction-shaped record by
 * automatically selecting the correct ERP field names (Agave vs. Flex) based on
 * the active coding system, then formatting each value via getDisplayName.
 *
 * Eliminates the need to manually map ERP fields in every component that displays
 * coding data (e.g. MobileTransactionDetails, MobileReimbursementDetails).
 */
export function useCodingDisplayValues(
  record: Record<string, unknown> | null | undefined
): CodingDisplayValues | null {
  const { codingSystemType } = useCodingSystemConfig();
  const { getDisplayName } = useCodingDisplay();
  // SEE-1154: Sage cost codes resolve from the id alone to a single collapsed
  // name; fetch the selected project's name and overlay it below. Self-contained
  // (this hook renders one record per detail screen). Additive: no-op unless a
  // per-project name exists.
  const perProjectTxns = useMemo(
    () => (record ? [record as unknown as HighnoteTransactionEvent] : []),
    [record]
  );
  const getPerProjectCostCodeName = usePerProjectCostCodeNames(perProjectTxns);

  return useMemo(() => {
    if (!record || !codingSystemType || codingSystemType === 'jobCode') return null;

    const fieldMap = codingSystemType === 'agave' ? AGAVE_FIELD_MAP : FLEX_FIELD_MAP;

    return CODING_FIELD_CONFIGS.reduce((acc, config) => {
      // SEE-1135 — the billable item is stored as two flat fields
      // (erpBillableItemId + denormalized erpBillableItemName), not a single
      // object field, so it has no AGAVE_FIELD_MAP entry. Reconstruct the
      // ErpPostingAccount shape here so getDisplayName renders "Name - (ITEMID)".
      if (config.id === 'billableItem') {
        const id = record.erpBillableItemId as string | undefined;
        const name = record.erpBillableItemName as string | undefined;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        acc.billableItem = id ? getDisplayName({ accountId: id, accountName: name ?? id } as any) : '';
        return acc;
      }
      const fieldName = fieldMap[config.id];

      // SEE-1154: for Agave cost codes, overlay the project-scoped name before
      // formatting so an already-coded transaction shows the selected project's
      // name instead of the single collapsed AgaveCostCode.name.
      if (config.id === 'costCode' && codingSystemType === 'agave' && fieldName) {
        const costCode = record[fieldName] as
          | { id?: string; name?: string; fullName?: string }
          | null
          | undefined;
        const projectId =
          (record.agaveProjectId as string | undefined) ??
          (record.agaveProject as { id?: string } | undefined)?.id;
        const perProjectName = costCode?.id ? getPerProjectCostCodeName(projectId, costCode.id) : undefined;
        const displayCostCode =
          perProjectName && costCode
            ? { ...costCode, name: perProjectName, fullName: perProjectName.toLowerCase() }
            : costCode;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        acc[config.valueKey] = (displayCostCode ? getDisplayName(displayCostCode as any) : '') || '';
        return acc;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      acc[config.valueKey] = (fieldName ? getDisplayName(record[fieldName] as any) : '') || '';
      return acc;
    }, {} as CodingDisplayValues);
  }, [record, codingSystemType, getDisplayName, getPerProjectCostCodeName]);
}
